import { initializeShortcut } from './initializeStateFromDefinition';
import { State, Action, ConflictKeys, KeyboardShortcutState } from './types';
import { shortcutToString } from '../utils/shortcutToString';
import { sortShortcut } from '../utils/sortShortcut';

export function getPotencialConflict(state: State, id: string, newKeys?: string) {
	const toCompare = state.shortcuts[id];

	// early exits

	// if we're checking by changing active, skip if we're changing to inactive
	if (!newKeys && toCompare.isActive) {
		return null;
	} else if (newKeys && !toCompare.isActive) {
		// if we're checking by changing keyboard, only check if we're changing an
		// active keyboard
		return null;
	}

	// const keysToCompre = newKeys ?? toCompare?.keys;
	const potencialConflict = Object.keys(state.shortcuts)
		.filter(currentId => {
			const shortcut = state.shortcuts[currentId];
			if (shortcut?.isLocal) {
				return false;
			}
			return true;
		})
		.find(currentId => {
			if (currentId === id) {
				return false;
			}
			const current = state.shortcuts[currentId];
			// check conflict by changing keys (without taking key press order into consideration)
			if (newKeys) {
				if (current.isActive && sortShortcut(shortcutToString(current.keys)) === sortShortcut(shortcutToString(newKeys))) {
					return true;
				} else {
					return false;
				}
			} else {
				// check conflict by activating shortcut (without taking key press order into consideration)

				if (sortShortcut(shortcutToString(toCompare.keys)) === sortShortcut(shortcutToString(current.keys)) && current.isActive) {
					return true;
				} else {
					return false;
				}
			}
		});
	return potencialConflict ?? null;
}

export function keyboardShortcutReducer(state: State, action: Action): State {
	switch (action.type) {
		case 'DESACTIVATE_SHORTCUT_BY_ID': {
			const { id } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}
			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						isActive: false,
					},
				},
			};
		}
		case 'ADD_HANDLER': {
			const { id, handler } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}
			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						handler,
					},
				},
			};
		}
		case 'TOGGLE_IS_ACTIVE': {
			const { id } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}
			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						isActive: !state.shortcuts[id].isActive,
					},
				},
			};
		}
		case 'SET_IS_IN_PAGE': {
			const { id, isInPage } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}
			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						isInPage: isInPage,
						instances: isInPage ? state.shortcuts[id].instances + 1 : state.shortcuts[id].instances - 1,
					},
				},
			};
		}
		case 'SET_SHORTCUT_KEYS': {
			const { id, keys } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}

			const newState = {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						keys,
					},
				},
			};

			return newState;
		}
		case 'RESET_SHORTCUT_KEYS': {
			const { id } = action.payload;
			if (!state.shortcuts[id]) {
				return state;
			}
			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					[id]: {
						...state.shortcuts[id],
						keys: state.shortcuts[id].initialKeys,
					},
				},
			};
		}
		case 'RESET_ALL_SHORTCUT_KEYS': {
			return {
				...state,
				shortcuts: Object.keys(state.shortcuts).reduce(
					(acc, cur) => ({
						...acc,
						[cur]: {
							...state.shortcuts[cur],
							keys: state.shortcuts[cur].initialKeys,
						},
					}),
					{},
				),
			};
		}
		case 'IMPORT_SHORTCUTS': {
			const { contents } = action.payload;

			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					...Object.keys(contents).reduce((acc, cur) => {
						if (!state.shortcuts[cur]) {
							// shortcut doesn't exist
							return acc;
						}

						return {
							...acc,
							[cur]: {
								...state.shortcuts[cur],
								...contents[cur],
							},
						};
					}, {}),
				},
			};
		}
		case 'OVERRIDE_CONNFLICT': {
			const { type } = action.payload;

			if (type === 'KEYS') {
				const { conflictingKey, keys, id } = action.payload as ConflictKeys;
				if (!state.shortcuts[id] || !state.shortcuts[conflictingKey]) {
					return state;
				}
				return {
					...state,
					shortcuts: {
						...state.shortcuts,
						[id]: {
							...state.shortcuts[id],
							keys,
						},
						[conflictingKey]: {
							...state.shortcuts[conflictingKey],
							isActive: false,
						},
					},
				};
			} else {
				const { conflictingKey, id } = action.payload;
				if (!state.shortcuts[id] || !state.shortcuts[conflictingKey]) {
					return state;
				}

				return {
					...state,
					shortcuts: {
						...state.shortcuts,
						[id]: {
							...state.shortcuts[id],
							isActive: true,
						},
						[conflictingKey]: {
							...state.shortcuts[conflictingKey],
							isActive: false,
						},
					},
				};
			}
		}
		case 'ADD_CHILD_SHORTCUTS': {
			const { shortcuts } = action.payload;

			return {
				...state,
				shortcuts: {
					...state.shortcuts,
					...shortcuts.reduce(
						(acc: Record<string, KeyboardShortcutState>, cur) => ({
							...acc,
							[cur.id]: initializeShortcut(cur),
						}),
						{},
					),
				},
			};
		}
		case 'SYNC_FROM_PARENT': {
			const { state: childState } = action.payload;

			return {
				...state,
				shortcuts: {
					...childState.shortcuts,
				},
			};
		}
		default: {
			throw new Error(`Unhandled action type: ${JSON.stringify(action, null, 2)}`);
		}
	}
}
